import { ofetch } from 'ofetch';
import { log } from '@shared/log.browser.ts';
import type { NavigationHistory } from '@shared/types/History.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import type { TileListPageSession } from '@tilelist/application/core/TileListSession.ts';
import { createPageSession } from '@tilelist/application/core/TileListSession.ts';
import type { ClientReloadContextProvider } from '@tilelist/application/ports/ClientReloadContextProvider.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { TileListPageFetcher } from '@tilelist/application/ports/TileListPageFetcher.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import { EvergladesClient } from '@tilelist/clients/EvergladesClient.ts';
import { FullTileListStrategy } from '@tilelist/domain/composition/strategies/FullTileListStrategy.ts';
import type { PageLoadStrategy } from '@tilelist/domain/composition/strategies/PageLoadStrategy.ts';
import type { TileItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import { createCacheKeyRefresher } from '@tilelist/services/cache/CacheKeyRefresher.browser.ts';
import { restoreBySelector, saveScrollSelector } from '@tilelist/services/cache/ScrollPositionCache.browser.ts';
import { maybeRestoreFromCache, saveToCache } from '@tilelist/services/cache/TileListCache.browser.svelte.ts';
import { createEventsIncoming } from '@tilelist/services/events/EventsIncoming.browser.ts';
import { createEventsOutgoing } from '@tilelist/services/events/EventsOutgoing.browser.ts';
import { createNavigationHistory } from '@tilelist/services/history/NavigationHistory.browser.ts';
import { getTrackingService, getVariationLoadingService } from '@tilelist/services/ServiceFactory.browser.ts';
import { trackAdjustForTileList } from '@tilelist/services/tracking/AppsTracking.browser.ts';
import { trackFirebase } from '@tilelist/services/tracking/FirebaseTracking.browser.ts';
import { frontendPageViewTracking } from '@tilelist/services/tracking/PageViewTracking.browser.ts';
import { createTrackingClientReloadContextProvider } from '@tilelist/services/TrackingClientReloadContextProvider.browser.ts';

export type StrategyFactory = (variationLoader: VariationLoader) => PageLoadStrategy;

export type TileListBrowserWiring = {
    session: TileListPageSession;
    eventsIncoming: EventsIncoming;
    eventsOutgoing: EventsOutgoing;
    navigationHistory: NavigationHistory;
    trackingService: TrackingPort;
    variationLoadingService: VariationLoader;
    pageFetcher: TileListPageFetcher;
    reloadContextProvider: ClientReloadContextProvider;
    restoreScrollPosition(): Promise<void>;
    saveScrollSelector(): void;
    maybeRestoreFromCache(pageView: TileListPageLayout): Promise<TileListPageLayout>;
    sendTracking(pageView: TileListPageLayout, historic?: boolean): void;
    restoreAndTrackInitialPage(pageView: TileListPageLayout): Promise<TileListPageLayout>;
    onRedirectToPdp(item: TileItem, currentPageView: TileListPageLayout | null): void;
};

/**
 *
 *
 *
 *
 *
 */
export function createTileListBrowserWiring(
    initialSession: TileListInitialSession,
    navigationHistoryEnabled: boolean,
    strategyFactory?: StrategyFactory,
): TileListBrowserWiring {
    const trackingService = getTrackingService();
    const variationLoadingService = getVariationLoadingService();
    const eventsOutgoing = createEventsOutgoing();
    const eventsIncoming = createEventsIncoming();
    const navigationHistory = createNavigationHistory();
    trackingService.configureNavigationHistory(navigationHistory, navigationHistoryEnabled);
    const reloadContextProvider = createTrackingClientReloadContextProvider(trackingService);

    const evergladesClient = new EvergladesClient({
        fetchClient: ofetch,
        logger: log,
        entryRule: initialSession.session.entryRule,
        timeout: 10000,
    });

    const strategy = strategyFactory
        ? strategyFactory(variationLoadingService)
        : new FullTileListStrategy(variationLoadingService);

    const session = createPageSession(initialSession, {
        pageFetcher: evergladesClient,
        variationLoadingService,
        reloadContextProvider,
        eventPublisher: eventsOutgoing,
        cacheKeyRefresher: createCacheKeyRefresher(),
        strategy,
    });

    function sendTracking(pageView: TileListPageLayout, historic?: boolean): void {
        const pageViewTracking = frontendPageViewTracking(!!historic);
        trackingService.trackPageView(pageView, pageViewTracking);
        trackFirebase(eventsOutgoing, pageView.tileListItems);
        trackAdjustForTileList(pageView.tileListItems, session.onex);
    }

    async function restoreAndTrackInitialPage(pageView: TileListPageLayout): Promise<TileListPageLayout> {
        const restored = await maybeRestoreFromCache(pageView);
        sendTracking(restored);
        return restored;
    }

    function onRedirectToPdp(_item: TileItem, currentPageView: TileListPageLayout | null): void {
        saveScrollSelector();
        if (currentPageView) {
            void saveToCache(currentPageView);
        }
    }

    return {
        session,
        eventsIncoming,
        eventsOutgoing,
        navigationHistory,
        trackingService,
        variationLoadingService,
        pageFetcher: evergladesClient,
        reloadContextProvider,
        restoreScrollPosition: restoreBySelector,
        saveScrollSelector,
        maybeRestoreFromCache,
        sendTracking,
        restoreAndTrackInitialPage,
        onRedirectToPdp,
    };
}
